import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, Button, Input } from '../components/ui'
import { PageHeader } from '../../ui'
import { supabase } from '../../services/supabase'
import {
  adminListUsers,
  adminListSites,
  adminListInvitations,
  adminUserExists,
  adminInvitationByEmail,
  adminCreateInvitation,
  adminDeleteInvitation,
  adminUpdateUser,
  adminDeleteUser,
} from '../lib/ixverifyApi'
import { useIXVerifyAuth } from '../contexts'
import type { User, UserRole, Site } from '../types'

interface UserWithSite extends User {
  site?: Site | null
}

interface Invitation {
  id: string
  email: string
  site_id: string | null
  role: UserRole
  invited_by: string | null
  token: string
  expires_at: string
  accepted_at: string | null
  created_at: string
  inviter?: { full_name: string | null; email: string } | null
}

export function UserManagement() {
  const { user: currentUser, loading: authLoading } = useIXVerifyAuth()
  const [users, setUsers] = useState<UserWithSite[]>([])
  const [sites, setSites] = useState<Site[]>([])
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [editingUser, setEditingUser] = useState<UserWithSite | null>(null)
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<UserWithSite | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Form state for editing users
  const [formEmail, setFormEmail] = useState('')
  const [formFullName, setFormFullName] = useState('')
  const [formRole, setFormRole] = useState<UserRole>('viewer')
  const [formSiteId, setFormSiteId] = useState<string>('')

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<UserRole>('viewer')
  const [inviteSiteId, setInviteSiteId] = useState<string>('')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState('')
  const [inviteSuccess, setInviteSuccess] = useState(false)

  // Bulk invite state
  const [bulkMode, setBulkMode] = useState(false)
  const [bulkEmails, setBulkEmails] = useState('')
  const [bulkResults, setBulkResults] = useState<{ email: string; status: 'pending' | 'success' | 'error'; message?: string }[]>([])
  const [bulkInviteComplete, setBulkInviteComplete] = useState(false)

  // Check if current user is admin or superadmin
  const isSuperAdmin = currentUser?.role === 'superadmin'
  const isAdmin = currentUser?.role === 'admin' || isSuperAdmin

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return

    if (isAdmin) {
      fetchData()
    } else {
      setLoading(false)
    }
  }, [isAdmin, authLoading])

  const fetchData = async () => {
    setLoading(true)
    try {
      // Regular admins only see their own site; superadmins see all.
      const scopeSiteId = (!isSuperAdmin && currentUser?.site_id) ? currentUser.site_id : undefined

      // Fetch users with their site info
      const { data: usersData, error: usersError } = await adminListUsers({ siteId: scopeSiteId })

      if (usersError) throw usersError

      // Fetch sites - regular admins only see their own site
      const { data: sitesData, error: sitesError } = await adminListSites({ siteId: scopeSiteId })

      if (sitesError) throw sitesError

      // Fetch pending, not-yet-expired invitations
      const { data: invitationsData, error: invitationsError } = await adminListInvitations({
        pending: true,
        notExpired: true,
        siteId: scopeSiteId,
      })

      if (invitationsError) {
        console.error('Error fetching invitations:', invitationsError)
      }

      setUsers(usersData || [])
      setSites(sitesData || [])
      setInvitations(invitationsData || [])
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredUsers = users.filter(
    (u) =>
      (u.full_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const roleColors: Record<UserRole, string> = {
    superadmin: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
    admin: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
    coordinator: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
    viewer: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
  }

  const roleLabels: Record<UserRole, string> = {
    superadmin: 'Super Admin',
    admin: 'Admin',
    coordinator: 'Coordinator',
    viewer: 'Viewer',
  }

  const resetForm = () => {
    setFormEmail('')
    setFormFullName('')
    setFormRole('viewer')
    setFormSiteId('')
    setError('')
    setEditingUser(null)
  }

  const resetInviteForm = () => {
    setInviteEmail('')
    setInviteRole('viewer')
    setInviteSiteId(isSuperAdmin ? '' : currentUser?.site_id || '')
    setInviteError('')
    setInviteSuccess(false)
    setBulkMode(false)
    setBulkEmails('')
    setBulkResults([])
    setBulkInviteComplete(false)
    setShowInviteModal(false)
  }

  const openInviteModal = () => {
    setInviteEmail('')
    setInviteRole('viewer')
    // For non-superadmins, default to their site
    setInviteSiteId(isSuperAdmin ? '' : currentUser?.site_id || '')
    setInviteError('')
    setInviteSuccess(false)
    setShowInviteModal(true)
  }

  const handleInviteUser = async () => {
    // Validation
    if (!inviteEmail.trim()) {
      setInviteError('Email is required')
      return
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(inviteEmail.trim())) {
      setInviteError('Please enter a valid email address')
      return
    }

    // For non-superadmins, site is required
    const siteId = isSuperAdmin ? inviteSiteId : currentUser?.site_id
    if (!siteId) {
      setInviteError('Site is required')
      return
    }

    // Only superadmins can invite superadmins
    if (inviteRole === 'superadmin' && !isSuperAdmin) {
      setInviteError('Only super admins can invite other super admins')
      return
    }

    setInviting(true)
    setInviteError('')

    try {
      // Check if user already exists
      const { data: existingUserResult } = await adminUserExists(inviteEmail.trim().toLowerCase())

      if (existingUserResult?.exists) {
        setInviteError('A user with this email already exists')
        setInviting(false)
        return
      }

      // Check if there's already a pending invitation
      const { data: existingInvitationResult } = await adminInvitationByEmail(inviteEmail.trim().toLowerCase())

      if (existingInvitationResult?.exists) {
        setInviteError('A pending invitation already exists for this email')
        setInviting(false)
        return
      }

      // Create the invitation
      const { data: invitation, error: insertError } = await adminCreateInvitation({
        email: inviteEmail.trim().toLowerCase(),
        site_id: siteId,
        role: inviteRole,
      })

      if (insertError) throw insertError

      // Get site name for the email
      const site = sites.find((s) => s.id === siteId)

      // Send invitation email via Edge Function
      const { error: emailError } = await supabase.functions.invoke('send-invitation', {
        body: {
          email: inviteEmail.trim().toLowerCase(),
          inviterName: currentUser?.full_name || currentUser?.email || 'Administrator',
          siteName: site?.name || '',
          role: inviteRole,
          token: invitation.token,
        },
      })

      if (emailError) {
        console.error('Error sending invitation email:', emailError)
        // Don't fail the whole operation if email fails - invitation is still created
      }

      // Refresh the invitations list
      setInvitations([
        {
          ...invitation,
          inviter: { full_name: currentUser?.full_name || null, email: currentUser?.email || '' },
        },
        ...invitations,
      ])

      setInviteSuccess(true)
      setTimeout(() => {
        resetInviteForm()
      }, 2000)
    } catch (error) {
      console.error('Error creating invitation:', error)
      setInviteError('Failed to create invitation. Please try again.')
    } finally {
      setInviting(false)
    }
  }

  // Parse emails from bulk input (supports comma, semicolon, newline, space separation)
  const parseEmails = (input: string): string[] => {
    return input
      .split(/[\s,;\n]+/)
      .map((email) => email.trim().toLowerCase())
      .filter((email) => email.length > 0)
  }

  // Get valid emails from bulk input
  const getValidEmails = (): { valid: string[]; invalid: string[] } => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const emails = parseEmails(bulkEmails)
    const valid: string[] = []
    const invalid: string[] = []
    const seen = new Set<string>()

    for (const email of emails) {
      if (seen.has(email)) continue // Skip duplicates
      seen.add(email)

      if (emailRegex.test(email)) {
        valid.push(email)
      } else {
        invalid.push(email)
      }
    }
    return { valid, invalid }
  }

  const handleBulkInvite = async () => {
    const { valid: validEmails, invalid: invalidEmails } = getValidEmails()

    if (validEmails.length === 0) {
      setInviteError('No valid email addresses found')
      return
    }

    // For non-superadmins, site is required
    const siteId = isSuperAdmin ? inviteSiteId : currentUser?.site_id
    if (!siteId) {
      setInviteError('Site is required')
      return
    }

    // Only superadmins can invite superadmins
    if (inviteRole === 'superadmin' && !isSuperAdmin) {
      setInviteError('Only super admins can invite other super admins')
      return
    }

    setInviting(true)
    setInviteError('')

    // Initialize results with invalid emails marked as errors
    const initialResults = [
      ...invalidEmails.map((email) => ({ email, status: 'error' as const, message: 'Invalid email format' })),
      ...validEmails.map((email) => ({ email, status: 'pending' as const })),
    ]
    setBulkResults(initialResults)

    const site = sites.find((s) => s.id === siteId)
    const newInvitations: Invitation[] = []

    // Process each valid email
    for (let i = 0; i < validEmails.length; i++) {
      const email = validEmails[i]

      try {
        // Check if user already exists
        const { data: existingUserResult } = await adminUserExists(email)

        if (existingUserResult?.exists) {
          setBulkResults((prev) =>
            prev.map((r) => (r.email === email ? { ...r, status: 'error', message: 'User already exists' } : r))
          )
          continue
        }

        // Check if there's already a pending invitation
        const { data: existingInvitationResult } = await adminInvitationByEmail(email)

        if (existingInvitationResult?.exists) {
          setBulkResults((prev) =>
            prev.map((r) => (r.email === email ? { ...r, status: 'error', message: 'Invitation already pending' } : r))
          )
          continue
        }

        // Create the invitation
        const { data: invitation, error: insertError } = await adminCreateInvitation({
          email,
          site_id: siteId,
          role: inviteRole,
        })

        if (insertError) throw insertError

        // Send invitation email
        await supabase.functions.invoke('send-invitation', {
          body: {
            email,
            inviterName: currentUser?.full_name || currentUser?.email || 'Administrator',
            siteName: site?.name || '',
            role: inviteRole,
            token: invitation.token,
          },
        })

        newInvitations.push({
          ...invitation,
          inviter: { full_name: currentUser?.full_name || null, email: currentUser?.email || '' },
        })

        setBulkResults((prev) =>
          prev.map((r) => (r.email === email ? { ...r, status: 'success', message: 'Invitation sent' } : r))
        )
      } catch (error) {
        console.error(`Error inviting ${email}:`, error)
        setBulkResults((prev) =>
          prev.map((r) => (r.email === email ? { ...r, status: 'error', message: 'Failed to send' } : r))
        )
      }
    }

    // Update invitations list with new ones
    if (newInvitations.length > 0) {
      setInvitations([...newInvitations, ...invitations])
    }

    setBulkInviteComplete(true)
    setInviting(false)
  }

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const { error: deleteError } = await adminDeleteInvitation(invitationId)

      if (deleteError) throw deleteError

      setInvitations(invitations.filter((i) => i.id !== invitationId))
    } catch (error) {
      console.error('Error canceling invitation:', error)
    }
  }

  const handleResendInvitation = async (invitation: Invitation) => {
    try {
      const site = sites.find((s) => s.id === invitation.site_id)

      const { error: emailError } = await supabase.functions.invoke('send-invitation', {
        body: {
          email: invitation.email,
          inviterName: currentUser?.full_name || currentUser?.email || 'Administrator',
          siteName: site?.name || '',
          role: invitation.role,
          token: invitation.token,
        },
      })

      if (emailError) throw emailError

      // Visual feedback could be added here
    } catch (error) {
      console.error('Error resending invitation:', error)
    }
  }

  const openEditModal = (user: UserWithSite) => {
    setEditingUser(user)
    setFormEmail(user.email)
    setFormFullName(user.full_name || '')
    setFormRole(user.role || 'viewer')
    setFormSiteId(user.site_id || '')
    setError('')
  }

  const handleUpdateUser = async () => {
    if (!editingUser) return

    // Prevent demoting yourself if you're the only admin
    if (editingUser.id === currentUser?.id && formRole !== 'admin') {
      const adminCount = users.filter((u) => u.role === 'admin').length
      if (adminCount <= 1) {
        setError('Cannot remove admin role. At least one admin is required.')
        return
      }
    }

    setSaving(true)
    setError('')

    try {
      // NOTE: site_id is managed via user_site_access, not the users table; the
      // API persists full_name + role (site_id is ignored server-side).
      const { error: updateError } = await adminUpdateUser(editingUser.id, {
        full_name: formFullName.trim() || null,
        role: formRole,
      })

      if (updateError) throw updateError

      // Update local state
      setUsers(
        users.map((u) =>
          u.id === editingUser.id
            ? {
                ...u,
                full_name: formFullName.trim() || null,
                role: formRole,
                site_id: formSiteId || null,
                site: sites.find((s) => s.id === formSiteId) || null,
              }
            : u
        )
      )
      resetForm()
    } catch (error) {
      console.error('Error updating user:', error)
      setError('Failed to update user. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleDeactivateUser = async (user: UserWithSite) => {
    // Prevent deactivating yourself
    if (user.id === currentUser?.id) {
      return
    }

    try {
      // Soft-delete (sets deleted_at) via the platform API — the standard platform
      // convention. Does not delete the Supabase Auth user (Phase 8).
      const { error: deleteError } = await adminDeleteUser(user.id)

      if (deleteError) throw deleteError

      setUsers(users.filter((u) => u.id !== user.id))
      setDeleteConfirmUser(null)
    } catch (error) {
      console.error('Error deactivating user:', error)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Not an admin
  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader title="User Management" subtitle="Manage system users and permissions" />
        <Card>
          <CardContent className="p-6 text-center">
            <svg className="w-12 h-12 mx-auto text-red-500 dark:text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-gray-900 dark:text-gray-100 font-semibold mb-2">Access Denied</p>
            <p className="text-gray-600 dark:text-gray-400">
              You need administrator privileges to manage users.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="User Management"
        subtitle="Manage system users and permissions"
        actions={
          <Button size="sm" onClick={openInviteModal}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Invite User
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400">
                {searchQuery ? 'No users match your search' : 'No users found'}
              </p>
            </div>
          ) : (
            <>
              {/* Stats Summary */}
              <div className="mb-4 flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                <span>{filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}</span>
                {isSuperAdmin && filteredUsers.filter((u) => u.role === 'superadmin').length > 0 && (
                  <span className="text-red-600 dark:text-red-400">
                    {filteredUsers.filter((u) => u.role === 'superadmin').length} super admins
                  </span>
                )}
                <span className="text-purple-600 dark:text-purple-400">
                  {filteredUsers.filter((u) => u.role === 'admin').length} admins
                </span>
                <span className="text-primary-600 dark:text-primary-400">
                  {filteredUsers.filter((u) => u.role === 'coordinator').length} coordinators
                </span>
                <span className="text-green-600 dark:text-green-400">
                  {filteredUsers.filter((u) => u.role === 'viewer').length} viewers
                </span>
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 dark:text-gray-400 border-b">
                      <th className="pb-3 font-medium">User</th>
                      <th className="pb-3 font-medium">Role</th>
                      <th className="pb-3 font-medium">Site</th>
                      <th className="pb-3 font-medium">Joined</th>
                      <th className="pb-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                              {user.avatar_url ? (
                                <img
                                  src={user.avatar_url}
                                  alt=""
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <span className="text-gray-600 dark:text-gray-400 font-medium">
                                  {(user.full_name || user.email).charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-gray-100">
                                {user.full_name || '-'}
                                {user.id === currentUser?.id && (
                                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(you)</span>
                                )}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3">
                          <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColors[user.role || 'viewer']}`}>
                            {roleLabels[user.role || 'viewer']}
                          </span>
                        </td>
                        <td className="py-3 text-gray-600 dark:text-gray-400">
                          {user.site?.name || <span className="text-gray-400 dark:text-gray-500">No site</span>}
                        </td>
                        <td className="py-3 text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditModal(user)}
                            >
                              Edit
                            </Button>
                            {user.id !== currentUser?.id && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDeleteConfirmUser(user)}
                                className="text-red-600 dark:text-red-400 border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((user) => (
                  <div key={user.id} className="py-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                          <span className="text-gray-600 dark:text-gray-400 font-medium">
                            {(user.full_name || user.email).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {user.full_name || '-'}
                            {user.id === currentUser?.id && (
                              <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">(you)</span>
                            )}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColors[user.role || 'viewer']}`}>
                        {roleLabels[user.role || 'viewer']}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        {user.site?.name || 'No site'} - Joined {formatDate(user.created_at)}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          Edit
                        </button>
                        {user.id !== currentUser?.id && (
                          <button
                            onClick={() => setDeleteConfirmUser(user)}
                            className="text-red-600 dark:text-red-400 hover:underline"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations Section */}
      {invitations.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Pending Invitations</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">{invitations.length} pending</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {invitations.map((invitation) => (
                <div key={invitation.id} className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/40 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{invitation.email}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColors[invitation.role]}`}>
                          {roleLabels[invitation.role]}
                        </span>
                        <span>-</span>
                        <span>Invited {formatDate(invitation.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleResendInvitation(invitation)}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      Resend
                    </button>
                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      className="text-sm text-red-600 dark:text-red-400 hover:underline"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={resetForm}></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit User</h3>
                <button onClick={resetForm} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formEmail}
                    disabled
                    className="bg-gray-50 dark:bg-gray-900/40"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Email cannot be changed
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name
                  </label>
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formFullName}
                    onChange={(e) => setFormFullName(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Role
                  </label>
                  <select
                    value={formRole}
                    onChange={(e) => setFormRole(e.target.value as UserRole)}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  >
                    <option value="viewer">Viewer - Can view verifications</option>
                    <option value="coordinator">Coordinator - Can perform verifications</option>
                    <option value="admin">Admin - Site administrator</option>
                    {isSuperAdmin && (
                      <option value="superadmin">Super Admin - System-wide access</option>
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Site
                  </label>
                  <select
                    value={formSiteId}
                    onChange={(e) => setFormSiteId(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  >
                    <option value="">No site assigned</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </select>
                </div>

                {error && (
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleUpdateUser}
                    disabled={saving}
                    className="flex-1"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                  <Button variant="outline" onClick={resetForm} disabled={saving}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setDeleteConfirmUser(null)}></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto text-red-500 dark:text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Remove User</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Are you sure you want to remove <strong>{deleteConfirmUser.full_name || deleteConfirmUser.email}</strong>?
                  This action cannot be undone.
                </p>
                <div className="flex gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setDeleteConfirmUser(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleDeactivateUser(deleteConfirmUser)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Remove User
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={resetInviteForm}></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Invite Users</h3>
                <button onClick={resetInviteForm} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Mode Toggle */}
              {!inviteSuccess && !bulkInviteComplete && (
                <div className="flex mb-4 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => setBulkMode(false)}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      !bulkMode ? 'bg-white text-primary-700 shadow-sm dark:bg-gray-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    Single
                  </button>
                  <button
                    onClick={() => setBulkMode(true)}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      bulkMode ? 'bg-white text-primary-700 shadow-sm dark:bg-gray-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    Bulk Import
                  </button>
                </div>
              )}

              {/* Single Invite Success */}
              {inviteSuccess ? (
                <div className="text-center py-6">
                  <svg className="w-12 h-12 mx-auto text-green-500 dark:text-green-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Invitation Sent!</h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    An invitation email has been sent to <strong>{inviteEmail}</strong>
                  </p>
                </div>
              ) : bulkInviteComplete ? (
                /* Bulk Invite Results */
                <div className="space-y-4">
                  <div className="text-center py-2">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Invitations Complete</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {bulkResults.filter((r) => r.status === 'success').length} of {bulkResults.length} invitations sent
                    </p>
                  </div>
                  <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                    {bulkResults.map((result, index) => (
                      <div key={index} className="px-3 py-2 flex items-center justify-between">
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">{result.email}</span>
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded-full ml-2 ${
                            result.status === 'success'
                              ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400'
                              : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'
                          }`}
                        >
                          {result.message}
                        </span>
                      </div>
                    ))}
                  </div>
                  <Button onClick={resetInviteForm} className="w-full">
                    Done
                  </Button>
                </div>
              ) : bulkMode ? (
                /* Bulk Invite Form */
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email Addresses
                    </label>
                    <textarea
                      placeholder="Paste email addresses here (separated by commas, spaces, or new lines)&#10;&#10;Example:&#10;john@example.com&#10;jane@example.com&#10;mike@example.com"
                      value={bulkEmails}
                      onChange={(e) => setBulkEmails(e.target.value)}
                      className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400 h-32 text-sm"
                    />
                    {bulkEmails && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {getValidEmails().valid.length} valid email{getValidEmails().valid.length !== 1 ? 's' : ''} found
                        {getValidEmails().invalid.length > 0 && (
                          <span className="text-red-500 dark:text-red-400">
                            , {getValidEmails().invalid.length} invalid
                          </span>
                        )}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Role (applies to all)
                    </label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as UserRole)}
                      className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                    >
                      <option value="viewer">Viewer - Can view verifications</option>
                      <option value="coordinator">Coordinator - Can perform verifications</option>
                      <option value="admin">Admin - Site administrator</option>
                      {isSuperAdmin && (
                        <option value="superadmin">Super Admin - System-wide access</option>
                      )}
                    </select>
                  </div>

                  {isSuperAdmin && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Site (applies to all)
                      </label>
                      <select
                        value={inviteSiteId}
                        onChange={(e) => setInviteSiteId(e.target.value)}
                        className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                      >
                        <option value="">Select a site</option>
                        {sites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {!isSuperAdmin && currentUser?.site_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Site
                      </label>
                      <p className="px-3 py-2 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300">
                        {sites.find((s) => s.id === currentUser.site_id)?.name || 'Your site'}
                      </p>
                    </div>
                  )}

                  {inviteError && (
                    <p className="text-sm text-red-600 dark:text-red-400">{inviteError}</p>
                  )}

                  {/* Progress during invite */}
                  {inviting && bulkResults.length > 0 && (
                    <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Sending invitations...</span>
                        <span className="text-sm font-medium">
                          {bulkResults.filter((r) => r.status !== 'pending').length} / {bulkResults.length}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${(bulkResults.filter((r) => r.status !== 'pending').length / bulkResults.length) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={handleBulkInvite}
                      disabled={inviting || getValidEmails().valid.length === 0}
                      className="flex-1"
                    >
                      {inviting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        `Send ${getValidEmails().valid.length} Invitation${getValidEmails().valid.length !== 1 ? 's' : ''}`
                      )}
                    </Button>
                    <Button variant="outline" onClick={resetInviteForm} disabled={inviting}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                /* Single Invite Form */
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      placeholder="user@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Role
                    </label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as UserRole)}
                      className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                    >
                      <option value="viewer">Viewer - Can view verifications</option>
                      <option value="coordinator">Coordinator - Can perform verifications</option>
                      <option value="admin">Admin - Site administrator</option>
                      {isSuperAdmin && (
                        <option value="superadmin">Super Admin - System-wide access</option>
                      )}
                    </select>
                  </div>

                  {isSuperAdmin && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Site
                      </label>
                      <select
                        value={inviteSiteId}
                        onChange={(e) => setInviteSiteId(e.target.value)}
                        className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                      >
                        <option value="">Select a site</option>
                        {sites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {!isSuperAdmin && currentUser?.site_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Site
                      </label>
                      <p className="px-3 py-2 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300">
                        {sites.find((s) => s.id === currentUser.site_id)?.name || 'Your site'}
                      </p>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Users will be invited to your site
                      </p>
                    </div>
                  )}

                  {inviteError && (
                    <p className="text-sm text-red-600 dark:text-red-400">{inviteError}</p>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={handleInviteUser}
                      disabled={inviting}
                      className="flex-1"
                    >
                      {inviting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        'Send Invitation'
                      )}
                    </Button>
                    <Button variant="outline" onClick={resetInviteForm} disabled={inviting}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
